import type { Locale } from 'config/constants/locales';
import { useEffect } from 'react';
import { usePrevious } from 'react-use';
import { useSWRConfig } from 'swr';

/**
 * Drops the whole SWR cache when the active locale changes.
 *
 * The language reaches the backend only through the `accept-language` header set in
 * `generateApiInterceptor`, and no SWR key encodes it. Switching language is a client-side
 * navigation, so without this every cached entry survives it and keeps serving the previous
 * language until it happens to be revalidated.
 *
 * Must run after `syncApiInterceptor` so the refetches carry the new header.
 */
export const useResetSwrOnLocaleChange = (locale: Locale): void => {
  const { mutate } = useSWRConfig();
  const previousLocale = usePrevious(locale);

  useEffect(() => {
    if (!previousLocale || previousLocale === locale) {
      return;
    }

    void mutate(
      () => {
        return true;
      },
      undefined,
      { revalidate: true, throwOnError: false }
    );
  }, [locale, previousLocale, mutate]);
};
